/*
 *   File : sales-reward-list.js
 *   Author : https://evoqins.com
 *   Description : OCR sales upload and reward list page
 *   Integrations :
 *   Version : 1.0.0
*/

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { components as ReactSelectComponents } from 'react-select';

//custom component
import { DataTableComponent } from '../../../Component/DataTable';
import { DownloadButton, PrimaryButton, TextButton } from '../../../Component/Buttons';
import { APIService } from '../../../Service';
import { Loader } from '../../../Component/Others';
import { EmptyScreen } from '../../../Component/EmptyComponent';
import { ApproveUploadModal, PreviewSalesBill, RejectModal, SalesHistoryModal, SuccessModal } from '../../../Component/Modal';
import { Icon } from '../../../Component/Icon';
import { toast } from 'react-toastify';

import { ReactComponent as ArrowDown } from "../../../Assets/Image/table-arrow-down.svg";
import { ReactComponent as ArrowUp } from "../../../Assets/Image/table-arrow-up.svg";
import { CustomDatePicker, CustomSearchInput, CustomSelectBox } from '../../../Component/FormElements';
import { format } from 'date-fns';

const REQUEST_TABS = [
    {
        id: 1,
        label: "Invoices for Review"
    },
    {
        id: 2,
        label: "Past invoices"
    },
    {
        id: 5,
        label: "Duplicate submissions"
    }
];

const ROWS_PER_PAGE = 20;

const TAB_SORT_FIELDS = {
    // Pending / Past (status 1–4): full invoice row fields
    1: ['invoice_id', 'receipt_no', 'sales_specialist', 'store', 'date_of_upload', 'customer_type', 'product_name', 'product_code', 'quantity', 'total_points', 'status'],
    2: ['invoice_id', 'receipt_no', 'sales_specialist', 'store', 'date_of_upload', 'customer_type', 'product_name', 'product_code', 'quantity', 'total_points', 'status'],
    // Duplicate / Failed (status 5–7): no product line fields
    5: ['invoice_id', 'receipt_no', 'sales_specialist', 'store', 'date_of_upload', 'customer_type', 'status'],
};

const TAB_FILTER_KEYS = {
    1: 'pending_invoice_filters',
    2: 'past_invoice_filters',
    5: 'failed_invoice_filters',
};

const getActiveTabFilters = (tabId, allFilters = {}) => {
    const tabFilters = allFilters[TAB_FILTER_KEYS[tabId]] || {};
    const customerTypes = tabFilters.customer_types || [];
    const customer_types = customerTypes.length
        ? (customerTypes[0]?.value === null
            ? customerTypes
            : [{ value: null, label: 'All' }, ...customerTypes])
        : [{ value: null, label: 'All' }];

    return {
        ...tabFilters,
        customer_types,
    };
};

const OCRSalesAndRewardList = () => {

    const modalContent = useRef({})
    const scrollRef = useRef(null);

    const navigate = useNavigate();
    const { state } = useLocation();

    const [tabIndex, setTabIndex] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    const isSortingRef = useRef(false);
    const skipListFetchRef = useRef(false);
    // When arriving from dashboard top-seller, wait for filter-data before first list fetch
    const pendingNavFilterRef = useRef(
        state?.store_id != null || state?.customer_id != null
    );

    const [selectedSalesHistoryData, setSelectedSalesHistoryData] = useState({});
    const [showSalesHistory, setShowSalesHistory] = useState(false);
    const [showSalesBillPreview, setShowSalesBillPreview] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);

    const [approveId, setApproveId] = useState(null);
    const [approveData, setApproveData] = useState(null);
    const [productCode, setProductCode] = useState(null);
    const [approveSuccess, setApproveSuccess] = useState(false);
    const [rejectId, setRejectId] = useState(null);

    const [storeID, setStoreID] = useState(null);
    const [viewDetail, setViewDetail] = useState(false);
    const [rejectedViewDetail, setRejectedViewDetail] = useState(false);

    const [ocrList, setOcrList] = useState({});
    const [modalBtnLoader, setModalBtnLoader] = useState(false);

    const [invoiceList, setInvoiceList] = useState({});
    const [invoiceLoader, setInvoiceLoader] = useState(true);

    const [storeDetailData, setStoreDetailData] = useState([]);
    const [storeData, setStoreData] = useState({});

    const [searchValue, setSearchValue] = useState("");
    const [toggleFilter, setToggleFilter] = useState(false);

    const [selectedStore, setSelectedStore] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);

    const [filterData, setFilterData] = useState({});


    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [startDateToggle, setStartDateToggle] = useState(false);

    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [endDateError, setEndDateError] = useState("");
    const [endDateToggle, setEndDateToggle] = useState(false);

    // Last confirmed custom range (saved on Proceed); restored when Custom is reopened
    const [appliedCustomStartDate, setAppliedCustomStartDate] = useState(null);
    const [appliedCustomEndDate, setAppliedCustomEndDate] = useState(null);

    const [dateInputMenu, setDateInputMenu] = useState(false);
    const dateRangeMenuRef = useRef(null);

    const [expandedRow, setExpandedRow] = useState({});

    const [editID, setEditID] = useState(null);
    const [revertSuccess, setRevertSuccess] = useState(false);

    const [customerId, setCustomerId] = useState(null);

    const [selectedSalesSpecialist, setSelectedSalesSpecialist] = useState(null);
    const [selectedProductName, setSelectedProductName] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [clearFilter, setClearFilter] = useState(false);

    const [selectedCustomerType, setSelectedCustomerType] = useState(null);
    const [downloadLoader, setDownloadLoader] = useState(false);

    const activeTabFilters = getActiveTabFilters(tabIndex, filterData);

    const _applyDefaultFilters = (tabId, allFilters = filterData) => {
        const tabFilters = getActiveTabFilters(tabId, allFilters);

        setSelectedSalesSpecialist(tabFilters.customer_id?.[0] || null);
        setSelectedStore(tabFilters.store_id?.[0] || null);
        setSelectedCustomerType(tabFilters.customer_types?.[0] || null);
        setSelectedDate(tabFilters.date_option?.[0] || null);

        const statusOptions = tabId === 5 ? tabFilters.failure_status : null;
        setSelectedStatus(statusOptions ? statusOptions[0] : null);
    };

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth' //scroll top
        });
    }, []);

    useEffect(() => {
        if (state !== null) {
            if (state?.tab_index) {
                setTabIndex(state.tab_index);
            }
            if (state?.current_page) {
                setCurrentPage(state.current_page);
            }
        } else {
            setTabIndex(1);
            setCurrentPage(1);
        }
    }, [state]);

    useEffect(() => {
        _getFilterData();
    }, []);

    useEffect(() => {
        if (tabIndex !== 0) {
            if (pendingNavFilterRef.current) {
                return;
            }
            if (skipListFetchRef.current) {
                skipListFetchRef.current = false;
                return;
            }
            _getOCRList();
        }
    }, [tabIndex, currentPage, searchValue, clearFilter]);

    useEffect(() => {
        if (showSalesHistory) {
            const sales_history_modal = new Modal(document.getElementById('sale-history-modal'));
            sales_history_modal.show();
        }
    }, [showSalesHistory]);

    useEffect(() => {
        if (showSalesBillPreview) {
            const sales_preview_modal = new Modal(document.getElementById('preview-sale-history-modal'));
            sales_preview_modal.show();
        }
    }, [showSalesBillPreview]);

    useEffect(() => {
        if (approveId !== null || editID !== null) {
            const approve_upload = new Modal(document.getElementById('approve-upload-modal'));
            approve_upload.show();
        }
    }, [approveId, editID]);

    useEffect(() => {
        if (approveSuccess === true) {
            const success_modal = new Modal(document.getElementById('success-modal'));
            success_modal.show();
        }
    }, [approveSuccess]);

    useEffect(() => {
        if (rejectId !== null) {
            const reject_modal = new Modal(document.getElementById('reject-modal'));
            reject_modal.show();
        }
    }, [rejectId]);

    useEffect(() => {
        if (startDateToggle === true) {
            const start_date = new Modal(document.getElementById('start-date-modal'));
            start_date.show();
        }
    }, [startDateToggle]);

    useEffect(() => {
        if (endDateToggle === true) {
            const end_date = new Modal(document.getElementById('end-date-modal'));
            end_date.show();
        }
    }, [endDateToggle]);

    useEffect(() => {
        if (!dateInputMenu) {
            return undefined;
        }

        const handleClickOutside = (event) => {
            if (!dateRangeMenuRef.current || dateRangeMenuRef.current.contains(event.target)) {
                return;
            }

            const isDateModalClick = event.target.closest('#start-date-modal, #end-date-modal');
            if (isDateModalClick) {
                return;
            }

            _closeCustomDateMenu();
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dateInputMenu, appliedCustomStartDate, appliedCustomEndDate]);

    // handle tab change 
    const _handleTabChange = (id) => {
        navigate(window.location.pathname, {
            state: {
                tab_index: id,
                current_page: 1
            }
        });
        setDataLoading(true);
        setViewDetail(false);
        setStoreID(null);
        setStoreData({});
        setRejectedViewDetail(false);
        setToggleFilter(false);

        _applyDefaultFilters(id, filterData);

        setDateInputMenu(false);
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setAppliedCustomStartDate(null);
        setAppliedCustomEndDate(null);

        setSearchValue("");

        setSortField(null);
        setSortDirection(null);

    };

    const _isCustomDateOption = (option) => Number(option?.value) === 4;

    const _openCustomDateMenu = () => {
        setSelectedStartDate(appliedCustomStartDate);
        setSelectedEndDate(appliedCustomEndDate);
        setDateInputMenu(true);
    };

    const _commitCustomDates = () => {
        setAppliedCustomStartDate(selectedStartDate);
        setAppliedCustomEndDate(selectedEndDate);
        setDateInputMenu(false);
    };

    const _closeCustomDateMenu = () => {
        // Discard in-progress edits; keep last Proceed / committed range
        setSelectedStartDate(appliedCustomStartDate);
        setSelectedEndDate(appliedCustomEndDate);
        setDateInputMenu(false);
    };

    const _clearCustomDates = () => {
        setDateInputMenu(false);
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setAppliedCustomStartDate(null);
        setAppliedCustomEndDate(null);
    };

    // Always open custom date panel when Custom option is clicked (even if already selected)
    const dateRangeSelectComponents = {
        Option: (props) => (
            <ReactSelectComponents.Option
                {...props}
                innerProps={{
                    ...props.innerProps,
                    onClick: (event) => {
                        props.innerProps?.onClick?.(event);
                        if (_isCustomDateOption(props.data)) {
                            setTimeout(() => _openCustomDateMenu(), 0);
                        }
                    },
                }}
            />
        ),
    };

    // start date toggle show an hide
    const _handleStartDateToggle = () => {
        setStartDateToggle(!startDateToggle);
    };

    // handle select start date
    const _handleSelectedStartDate = (day) => {

        setSelectedStartDate(day);

        setStartDateToggle(false);
        _handleCloseStartDateModal();
        setSelectedEndDate(null);
    };

    // handle close start date modal 
    const _handleCloseStartDateModal = () => {
        var button = document.getElementById("start-date-modal-close");
        button.click();
        setStartDateToggle(false);
    };


    // end date toggle show an hide
    const _handleEndDateToggle = () => {
        setEndDateToggle(!endDateToggle);
    };

    // handle select end date
    const _handleSelectedEndDate = (day) => {
        setSelectedEndDate(day);
        setAppliedCustomStartDate(selectedStartDate);
        setAppliedCustomEndDate(day);
        setEndDateToggle(false);
        _handleCloseEndDateModal();
        // Close popup so other filters remain usable
        setDateInputMenu(false);
    };

    // handle close end date modal 
    const _handleCloseEndDateModal = () => {
        var button = document.getElementById("end-date-modal-close");
        button.click();
        setEndDateToggle(false);
    };


    // handle page change 
    const _handlePageChange = (page_num) => {
        // Ignore RDT's automatic page reset while we handle sort ourselves
        if (isSortingRef.current) {
            isSortingRef.current = false;
            return;
        }

        if (page_num === currentPage) {
            return;
        }

        setCurrentPage(page_num);
        navigate(window.location.pathname, {
            state: {
                tab_index: tabIndex,
                current_page: page_num
            }
        });

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
        setDataLoading(true);
    };

    const _handleSort = (column, direction) => {
        const sortBy = column.sortField || column.id || null;
        const allowedSortFields = TAB_SORT_FIELDS[tabIndex] || [];

        if (!sortBy || !allowedSortFields.includes(sortBy)) {
            return;
        }

        isSortingRef.current = true;
        setSortField(sortBy);
        setSortDirection(direction);
        setDataLoading(true);

        // Only skip the list useEffect when page will actually change.
        // If already on page 1, useEffect won't run and a stuck skip flag
        // would block the next pagination API call (loader stuck).
        if (currentPage !== 1) {
            skipListFetchRef.current = true;
        }
        setCurrentPage(1);
        navigate(window.location.pathname, {
            state: {
                tab_index: tabIndex,
                current_page: 1
            }
        });
        _getOCRList(1, sortBy, direction);

        setTimeout(() => {
            isSortingRef.current = false;
        }, 0);
    };

    const _handleClearAll = () => {
        setDataLoading(true);

        setClearFilter(true);
        setCurrentPage(1);
        setSortField(null);
        setSortDirection(null);
        setDateInputMenu(false);
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setAppliedCustomStartDate(null);
        setAppliedCustomEndDate(null);

        // Drop deep-link filters from history so a refresh does not re-apply them
        pendingNavFilterRef.current = false;
        navigate(window.location.pathname, {
            replace: true,
            state: {
                tab_index: tabIndex,
                current_page: 1
            }
        });

        _applyDefaultFilters(tabIndex, filterData);
    };


    //=========== API CAllS ============== //

    // API - get sales list
    const _getOCRList = (page_num, sort_by = sortField, sort_order = sortDirection, overrides = null) => {

        let updated_start_date = selectedStartDate !== null ? format(selectedStartDate, 'yyyy-MM-dd') : null;
        let updated_end_date = selectedEndDate !== null ? format(selectedEndDate, 'yyyy-MM-dd') : null;

        const activeStatus = overrides?.status != null
            ? overrides.status
            : (tabIndex === 5 ? (selectedStatus?.value || tabIndex) : tabIndex);
        const activeDateOption = overrides?.date_option !== undefined
            ? overrides.date_option
            : (selectedDate !== null ? selectedDate.value : null);
        const activeStartDate = overrides?.start_date !== undefined
            ? overrides.start_date
            : (selectedDate !== null && selectedDate.value === 4 ? updated_start_date : null);
        const activeEndDate = overrides?.end_date !== undefined
            ? overrides.end_date
            : (selectedDate !== null && selectedDate.value === 4 ? updated_end_date : null);

        let is_post = true;
        let url = 'CA/ocr/invoices';
        let data = {
            "page_num": page_num || currentPage,
            "limit": ROWS_PER_PAGE,
            "status": activeStatus,
            "search_query": searchValue || null,

            "customer_id": overrides?.customer_id !== undefined
                ? overrides.customer_id
                : (selectedSalesSpecialist?.value || null),
            "store_id": overrides?.store_id !== undefined
                ? overrides.store_id
                : (selectedStore?.value || null),
            // "category_id": selectedCategory?.value || null,
            "customer_type_id": selectedCustomerType?.value || null,
            // "product_name": selectedProductName?.value || null,

            "date_option": activeDateOption,
            "start_date": activeDateOption === 4 ? activeStartDate : null,
            "end_date": activeDateOption === 4 ? activeEndDate : null,
            "sort_by": sort_by ? [sort_by] : null,
            "order": sort_order ? [sort_order] : null,
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const list = response.data?.data || [];
                const updated_data = list.map((item, index) => ({
                    ...item,
                    _index: index
                }));

                setOcrList({
                    total_count: response.data.total_count,
                    data: updated_data
                });

                // setOcrList(response.data);
            } else {
                setOcrList({ data: [] });
            }
            setDataLoading(false);
            setClearFilter(false);
        });
    };


    // API - Download data
    const _download = () => {
        setDownloadLoader(true);

        let updated_start_date = selectedStartDate !== null ? format(selectedStartDate, 'yyyy-MM-dd') : null;
        let updated_end_date = selectedEndDate !== null ? format(selectedEndDate, 'yyyy-MM-dd') : null;

        let is_post = true;
        let url = "CA/ocr/download";
        let data = {
            "status": tabIndex === 5 ? (selectedStatus?.value || tabIndex) : tabIndex,
            "customer_id": selectedSalesSpecialist?.value || null,
            "store_id": selectedStore?.value || null,
            "customer_type_id": selectedCustomerType?.value || null,
            "date_option": selectedDate !== null ? selectedDate.value : null,
            "start_date": selectedDate !== null && selectedDate.value === 4 ? updated_start_date : null,
            "end_date": selectedDate !== null && selectedDate.value === 4 ? updated_end_date : null,
            "sort_by": sortField ? [sortField] : null,
            "order": sortDirection ? [sortDirection] : null,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const url = response.file_url;
                const link = document.createElement('a');
                link.href = url;
                link.download = '';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setDownloadLoader(false);
        });
    };

    // API - Reject product
    const _rejectProduct = (request) => {
        setModalBtnLoader(true);

        let is_post = true;
        let url = 'CA/ocr/reject-product';
        let data = {
            "product_id": request.id,
            "rejection_reason": request.message ? request.message : ""
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                modalContent.current = {
                    type: "reject",
                    title: "Rejected successfully",
                    description: "Successfully rejected the application."
                }
                setApproveSuccess(true);
            } else {
                setApproveSuccess(false);
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setModalBtnLoader(false);
        });
    };

    // API - Approve product
    const _approveProduct = (request) => {
        setModalBtnLoader(true);

        let is_post = true;
        let url = 'CA/ocr/approve-product';
        let data = {
            "id": request.id,
            "product_id": request.product_id,
            "quantity": request.quantity,
            "line_amount": request.line_amount,
            "product_description": request.product_description,
            "receipt_number": request.receipt_number,
            "receipt_date": request.receipt_date,
            "total_amount": request.total_amount,
            "corrected_comment": request.corrected_comment
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                modalContent.current = {
                    type: "success",
                    title: "Approved successfully",
                    description: "Successfully approved the application."
                }
                setApproveSuccess(true);
            } else {
                setApproveSuccess(false);
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setModalBtnLoader(false);
        });
    };

    // Apply store / specialist filters passed from dashboard top-seller navigation
    const _applyNavigationFilters = (allFilters = filterData) => {
        const activeTab = state?.tab_index || tabIndex || 2;
        const tabFilters = getActiveTabFilters(activeTab, allFilters);

        setSelectedCustomerType(tabFilters.customer_types?.[0] || null);
        setSelectedStatus(activeTab === 5 ? (tabFilters.failure_status?.[0] || null) : null);

        if (state?.store_id != null) {
            const storeOption = (tabFilters.store_id || []).find(
                (option) => String(option.value) === String(state.store_id)
            ) || {
                value: state.store_id,
                label: state.store_name || `Store ${state.store_id}`
            };
            setSelectedStore(storeOption);
        } else {
            setSelectedStore(tabFilters.store_id?.[0] || null);
        }

        if (state?.customer_id != null) {
            const specialistOption = (tabFilters.customer_id || []).find(
                (option) => String(option.value) === String(state.customer_id)
            ) || {
                value: state.customer_id,
                label: state.customer_name || `Specialist ${state.customer_id}`
            };
            setSelectedSalesSpecialist(specialistOption);
        } else {
            setSelectedSalesSpecialist(tabFilters.customer_id?.[0] || null);
        }

        const hasCustomDates = state?.start_date && state?.end_date;
        if (hasCustomDates && activeTab === 2) {
            const customDateOption = (tabFilters.date_option || []).find(
                (option) => option.value === 4
            ) || { value: 4, label: 'Custom' };
            const startDate = new Date(state.start_date);
            const endDate = new Date(state.end_date);
            setSelectedDate(customDateOption);
            setSelectedStartDate(startDate);
            setSelectedEndDate(endDate);
            setAppliedCustomStartDate(startDate);
            setAppliedCustomEndDate(endDate);
        } else {
            setSelectedDate(tabFilters.date_option?.[0] || null);
            setSelectedStartDate(null);
            setSelectedEndDate(null);
            setAppliedCustomStartDate(null);
            setAppliedCustomEndDate(null);
        }

        setToggleFilter(true);
        setDataLoading(true);

        pendingNavFilterRef.current = false;
        _getOCRList(state?.current_page || 1, null, null, {
            status: activeTab,
            store_id: state?.store_id ?? null,
            customer_id: state?.customer_id ?? null,
            date_option: hasCustomDates && activeTab === 2 ? 4 : null,
            start_date: hasCustomDates && activeTab === 2 ? state.start_date : null,
            end_date: hasCustomDates && activeTab === 2 ? state.end_date : null,
        });
    };

    // API - Get Filter data
    const _getFilterData = () => {
        let is_post = false;
        let url = 'CA/ocr/filter-data';
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setFilterData(response.data);
                const activeTab = state?.tab_index || tabIndex || 1;

                if (pendingNavFilterRef.current) {
                    _applyNavigationFilters(response.data);
                } else {
                    _applyDefaultFilters(activeTab, response.data);
                }
            } else if (pendingNavFilterRef.current) {
                // Still fetch list if filter options fail to load
                pendingNavFilterRef.current = false;
                _getOCRList(state?.current_page || 1, null, null, {
                    status: state?.tab_index || 2,
                    store_id: state?.store_id ?? null,
                    customer_id: state?.customer_id ?? null,
                    date_option: state?.start_date && state?.end_date ? 4 : null,
                    start_date: state?.start_date || null,
                    end_date: state?.end_date || null,
                });
            }
        });
    };

    // API - Revert product
    const _revertProduct = (request) => {
        setModalBtnLoader(true);

        let is_post = true;
        let url = 'CA/ocr/revert-product';
        let data = {
            "id": request.id,
            "product_id": request.product_id
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                navigate(window.location.pathname, {
                    state: {
                        tab_index: 1,
                        current_page: 1
                    }
                });
                setRevertSuccess(true);
                toast.dismiss();
                toast.success(response.message, {
                    type: "success",
                    className: "e-toast",
                });
            } else {
                setRevertSuccess(false)
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setModalBtnLoader(false);
        });
    };

    // Table columns
    const INVOICE_REVIEW_COLUMN = [
        {
            name: 'Invoice ID',
            id: 'invoice_id',
            sortField: 'invoice_id',
            selector: row => row.invoice_id || "-",
            width: '200px',
            sortable: true
        },
        {
            name: 'Sales specialist',
            id: 'sales_specialist',
            sortField: 'sales_specialist',
            selector: row => row.sales_specialist,
            cell: row => <>{row.sales_specialist}</>,
            width: '200px',
            sortable: true
        },
        {
            name: 'Store',
            id: 'store',
            sortField: 'store',
            selector: row => row.store_name,
            cell: row => <>
                {row.store_name || "-"}
            </>,
            width: '220px',
            sortable: true
        },
        {
            name: 'Date of upload',
            id: 'date_of_upload',
            sortField: 'date_of_upload',
            selector: row => row.date_of_upload || "-",
            width: '140px',
            sortable: true,
            format: row => row.date_of_upload,
        },
        {
            name: 'Customer type',
            id: 'customer_type',
            sortField: 'customer_type',
            selector: row => row.customer_type_text || "-",
            cell: row => <>
                {row.customer_type_text || "-"}
            </>,
            width: '170px',
            sortable: true
        },
        // {
        //     name: 'Product name',
        //     id: 'product_name',
        //     sortField: 'product_name',
        //     selector: row => row.product_info?.[0]?.name || row.product_name || "-",
        //     cell: row => <>
        //         {row.product_info?.[0]?.name || row.product_name || "-"}
        //     </>,
        //     width: '220px',
        //     sortable: true
        // },
        // {
        //     name: 'Product code',
        //     id: 'product_code',
        //     sortField: 'product_code',
        //     selector: row => row.product_info?.[0]?.code || row.product_code || "-",
        //     cell: row => <>
        //         {row.product_info?.[0]?.code || row.product_code || "-"}
        //     </>,
        //     width: '160px',
        //     sortable: true
        // },
        {
            name: 'Quantity',
            id: 'quantity',
            sortField: 'quantity',
            selector: row => row.total_quantity || "-",
            cell: row => <>
                {row.total_quantity_text || "-"}
            </>,
            width: '110px',
            sortable: true
        },
        {
            name: 'Total Points',
            id: 'total_points',
            sortField: 'total_points',
            selector: row => row.total_points || "-",
            width: '120px',
            sortable: true
        },
        {
            name: 'View invoice',
            cell: (row) => (
                <Icon
                    icon="invoice-icon"
                    size={20}
                    className="cursor-pointer"
                    onPress={() => {
                        setSelectedSalesHistoryData(row.file_url);
                        setShowSalesBillPreview(true);
                    }}
                />
            ),
            width: '120px',
        },
        {
            name: 'Status',
            id: 'status',
            sortField: 'status',
            selector: row => row.status,
            cell: row =>
                <>
                    {row.status ?
                        <span className={`${row.status.toLowerCase() === "active" ? "e-text-philippine-green" : "e-text-bright-orange"}  e-font-weight-500`}>
                            {row.status}
                        </span>
                        :
                        "-"
                    }
                </>,
            width: "160px",
            sortable: true
        },
        {
            name: 'Action',
            cell: row =>
                <>
                    <TextButton name="View"
                        className=" e-font-weight-400 cursor-pointer"
                        icon={true}
                        onPress={() => {
                            navigate("detail", {
                                state: {
                                    id: row.invoice_id
                                }
                            })
                        }}
                    />
                </>,
            width: "100px"
        },
    ];


    // Table columns
    const PAST_COLUMN = [
        {
            name: 'Invoice ID',
            id: 'invoice_id',
            sortField: 'invoice_id',
            selector: row => row.invoice_id || "-",
            width: '200px',
            sortable: true
        },
        {
            name: 'Receipt no',
            id: 'receipt_no',
            sortField: 'receipt_no',
            selector: row => row.reciept_no || row.receipt_no || "-",
            cell: row => <>{row.reciept_no || row.receipt_no || "-"}</>,
            width: '160px',
            sortable: true
        },
        {
            name: 'Sales specialist',
            id: 'sales_specialist',
            sortField: 'sales_specialist',
            selector: row => row.sales_specialist,
            cell: row => <>{row.sales_specialist}</>,
            width: '200px',
            sortable: true
        },
        {
            name: 'Store',
            id: 'store',
            sortField: 'store',
            selector: row => row.store_name || "-",
            cell: row => <>
                {row.store_name || "-"}
            </>,
            width: '220px',
            sortable: true
        },

        {
            name: 'Date of upload',
            id: 'date_of_upload',
            sortField: 'date_of_upload',
            selector: row => row.date_of_upload || "-",
            width: '140px',
            sortable: true,
            format: row => row.date_of_upload,
        },
        {
            name: 'Customer type',
            id: 'customer_type',
            sortField: 'customer_type',
            selector: row => row.customer_type_text || "-",
            cell: row => <>
                {row.customer_type_text || "-"}
            </>,
            width: '170px',
            sortable: true
        },
        // {
        //     name: 'Product name',
        //     id: 'product_name',
        //     sortField: 'product_name',
        //     selector: row => row.product_info?.[0]?.name || row.product_name || "-",
        //     cell: row => <>
        //         {row.product_info?.[0]?.name || row.product_name || "-"}
        //     </>,
        //     width: '220px',
        //     sortable: true
        // },
        // {
        //     name: 'Product code',
        //     id: 'product_code',
        //     sortField: 'product_code',
        //     selector: row => row.product_info?.[0]?.code || row.product_code || "-",
        //     cell: row => <>
        //         {row.product_info?.[0]?.code || row.product_code || "-"}
        //     </>,
        //     width: '160px',
        //     sortable: true
        // },
        {
            name: 'Quantity',
            id: 'quantity',
            sortField: 'quantity',
            selector: row => row.total_quantity || "-",
            cell: row => <>{row.total_quantity_text || "-"}</>,
            width: '120px',
            sortable: true
        },
        {
            name: 'Total Points',
            id: 'total_points',
            sortField: 'total_points',
            selector: row => row.total_points || "-",
            width: '120px',
            sortable: true
        },
        {
            name: 'View invoice',
            cell: (row) => (
                <Icon
                    icon="invoice-icon"
                    size={20}
                    className="cursor-pointer"
                    onPress={() => {
                        setSelectedSalesHistoryData(row.file_url);
                        setShowSalesBillPreview(true);
                    }}
                />
            ),
            width: '120px',
        },
        {
            name: 'Action',
            cell: row =>
                <>
                    <TextButton name="View"
                        className=" e-font-weight-400 cursor-pointer"
                        icon={true}
                        onPress={() => {
                            navigate("detail", {
                                state: {
                                    id: row.invoice_id
                                }
                            })
                        }}
                    />
                </>,
            width: "100px"
        },
    ];

    // Table columns - Duplicate submissions
    const DUPLICATE_COLUMN = [
        {
            name: 'Invoice ID',
            id: 'invoice_id',
            sortField: 'invoice_id',
            selector: row => row.invoice_id || "-",
            width: '200px',
            sortable: true
        },
        {
            name: 'Receipt no',
            id: 'receipt_no',
            sortField: 'receipt_no',
            selector: row => row.reciept_no || row.receipt_no || "-",
            cell: row => <>{row.reciept_no || row.receipt_no || "-"}</>,
            width: '160px',
            sortable: true
        },
        {
            name: 'Sales specialist',
            id: 'sales_specialist',
            sortField: 'sales_specialist',
            selector: row => row.sales_specialist,
            cell: row => <>{row.sales_specialist || "-"}</>,
            width: '200px',
            sortable: true
        },
        {
            name: 'Store',
            id: 'store',
            sortField: 'store',
            selector: row => row.store_name || "-",
            cell: row => <>
                {row.store_name || "-"}
            </>,
            width: '220px',
            sortable: true
        },
        {
            name: 'Date of upload',
            id: 'date_of_upload',
            sortField: 'date_of_upload',
            selector: row => row.date_of_upload || "-",
            width: '140px',
            sortable: true,
            format: row => row.date_of_upload,
        },
        {
            name: 'Customer type',
            id: 'customer_type',
            sortField: 'customer_type',
            selector: row => row.customer_type_text || "-",
            cell: row => <>
                {row.customer_type_text || "-"}
            </>,
            width: '200px',
            sortable: true
        },
        {
            name: 'View invoice',
            cell: (row) => {
                return (
                    <Icon
                        icon="invoice-icon"
                        size={20}
                        className="cursor-pointer"
                        onPress={() => {
                            setSelectedSalesHistoryData(row.file_url);
                            setShowSalesBillPreview(true);
                        }}
                    />
                );
            },
            width: '120px',
        },
        {
            name: 'Status',
            id: 'status',
            sortField: 'status',
            selector: row => row.status || "-",
            cell: row => <>{row.status || "-"}</>,
            width: "160px",
            sortable: true
        },
    ];

    const ExpandableRowsPast = ({ data }) => {
        if (!data.product_info || data.product_info.length === 0) {
            return <div className="p-3 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-16px e-bg-lotion-light">No data available.</div>;
        }

        const bgClass = data._index % 2 === 0 ? 'e-bg-magnolia' : 'e-bg-ghost-white';

        return (
            <div className={`py-2 padding-32px-right padding-76px-left d-flex flex-column gap-3 e-bg-lotion-light`}>
                {data.product_info.map((child_data, index) => (
                    <div key={index} className='d-flex align-items-center'>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px' style={{ width: "200px" }}>
                            "
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px' style={{ width: "220px" }}>
                            "
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px' style={{ width: "140px" }}>
                            "
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px pe-2' style={{ width: "300px" }}>
                            {child_data.name}
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px pe-2' style={{ width: "120px" }}>
                            {child_data.category || "-"}
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px pe-2' style={{ width: "140px" }}>
                            {child_data.code || "-"}
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px' style={{ width: "120px" }}>
                            {child_data.quantity || "-"}
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px' style={{ width: "120px" }}>
                            {child_data.points || "_"}
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px' style={{ width: "200px" }}>
                            "
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px' style={{ width: "120px" }}>
                            "
                        </p>
                        <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px' style={{ width: "120px" }}>
                            "
                        </p>
                    </div>
                ))}
            </div>
        );
    };


    return (
        <section className='pb-5 '>
            <div className='e-bg-lavender-blush-light padding-12px-top padding-98px-bottom padding-52px-lr'>
                <h1 className='mb-0 e-primary-font-playfair e-font-24 line-height-32px e-text-rich-black e-font-weight-600'>
                    Manage Sales Invoices
                </h1>
            </div>
            <div className='margin-neg-66px-top padding-52px-lr'>
                <div ref={scrollRef} className='e-bg-white e-border-1px-platinum-dark e-admin-box-shadow py-4 e-border-radius-16'>
                    <div className='px-3'>
                        <div className='row'>
                            <div className='col-xl-7 col-lg-6  col-6'>
                                <div className="d-flex gap-3">
                                    {
                                        REQUEST_TABS.map((tab) => (
                                            <div className={`${tabIndex === tab.id ? 'pe-none e-bg-lust e-text-white border-transparent' : 'e-border-1px-cool-grey e-text-rich-black cursor-pointer'}  e-border-radius-24 position-relative `}
                                                onClick={() => _handleTabChange(tab.id)} >
                                                <div className='d-flex align-items-center gap-2 px-3 padding-12px-tb'>
                                                    <p className='mb-0   e-alt-font-noto-sans  e-font-14 e-font-weight-500 line-height-14px w-max-content'>
                                                        {tab.label}
                                                    </p>

                                                    {/* {(tab.id === 1 || tab.id === 2) &&
                                                    <div className={`${tab.id === 1 ? "e-bg-carnelian" : "e-bg-apple"} d-flex align-items-center justify-content-center e-text-white e-alt-font-noto-sans  e-font-10 e-font-weight-400 line-height-12px rounded-circle `} style={{ width: "16px", height: "16px" }}>
                                                        {ocrList.total_count}
                                                    </div>
                                                } */}
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                            <div className='col-xl-5 col-lg-6  col-6'>
                                <div className='d-flex gap-3 align-items-center justify-content-end'>

                                    <DownloadButton loader={downloadLoader}
                                        onClick={_download} />

                                    {viewDetail === false && rejectedViewDetail === false &&
                                        <div className='position-relative'>
                                            <CustomSearchInput placeholder={"Search by name,ID or store"}
                                                type="text"
                                                require
                                                value={searchValue}
                                                className="w-348px padding-8px-tb py-2 "
                                                labelStyle="e-font-weight-500"
                                                iconClass="top-10px"
                                                handleChange={(e) => setSearchValue(e)}
                                                handleClear={() => { setSearchValue("") }} />
                                        </div>
                                    }
                                    {viewDetail === false && rejectedViewDetail === false &&
                                        <div>
                                            <img src={toggleFilter ? require("../../../Assets/Image/filter-icon-white.svg").default : require("../../../Assets/Image/filter-icon.svg").default}
                                                draggable={false}
                                                className={` ${toggleFilter ? `e-bg-indigo` : ``} transition e-border-1px-anti-flash-white e-border-radius-8 p-2 cursor-pointer`}
                                                alt='filter'
                                                onClick={() => {
                                                    setToggleFilter(!toggleFilter);
                                                }} />
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* filter */}
                    {toggleFilter &&
                        <div className='e-bg-anti-flash-white-dark py-3  px-4 mt-3'>
                            <div className='row'>
                                <div className='col-md-3 col-sm-4 col-4'>
                                    <CustomSelectBox label="Sale Specialist"
                                        value={selectedSalesSpecialist}
                                        options={activeTabFilters.customer_id || []}
                                        type={2}
                                        optional={true}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        isSearchable={false}
                                        onMenuOpen={_closeCustomDateMenu}
                                        selectChange={(value) => setSelectedSalesSpecialist(value)}
                                    />
                                </div>

                                <div className='col-md-3 col-sm-4 col-4'>
                                    <CustomSelectBox label="Store"
                                        value={selectedStore}
                                        options={activeTabFilters.store_id || []}
                                        type={2}
                                        optional={true}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        isSearchable={false}
                                        onMenuOpen={_closeCustomDateMenu}
                                        selectChange={(value) => setSelectedStore(value)}
                                    />
                                </div>

                                {tabIndex === 2 ?
                                    <>
                                        <div className='col-md-3 col-sm-4 col-4 '>
                                            <div className='position-relative' ref={dateRangeMenuRef}>
                                                <CustomSelectBox label="Date range"
                                                    value={selectedDate}
                                                    options={activeTabFilters.date_option || []}
                                                    optional={true}
                                                    type={2}
                                                    className="e-bg-white e-border-radius-4 e-table-select"
                                                    minHeight="34px"
                                                    padding="0 8px"
                                                    wrapperHeight="0px"
                                                    labelClass="e-font-weight-600"
                                                    isSearchable={false}
                                                    onMenuOpen={_closeCustomDateMenu}
                                                    components={dateRangeSelectComponents}
                                                    selectChange={(value) => {
                                                        if (_isCustomDateOption(value)) {
                                                            _openCustomDateMenu();
                                                        } else {
                                                            _clearCustomDates();
                                                        }
                                                        setSelectedDate(value);
                                                    }}
                                                />

                                                {dateInputMenu === true &&
                                                    <>
                                                        <div className='e-bg-white e-border-1px-platinum-light e-border-radius-8 p-3 w-100 position-absolute z-3 top-68px start-0' style={{ width: "240px" }}>

                                                            {/* Start date */}
                                                            <div id="single-product-startDate ">
                                                                <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-600 line-height-14px'>
                                                                    Start date
                                                                </p>

                                                                <div className='pt-1 position-relative'>
                                                                    <div className={`${startDateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} e-display-flex gap-0-4px e-border-radius-6 padding-10px-tb px-2 cursor-pointer`}
                                                                        onClick={_handleStartDateToggle}>
                                                                        <img src={require("../../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                                            draggable={false}
                                                                            alt='calender-icon' />
                                                                        {
                                                                            selectedStartDate !== null ?
                                                                                <>
                                                                                    <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                                        {format(selectedStartDate, 'dd-MM-yy')}
                                                                                    </p>

                                                                                </>
                                                                                :
                                                                                <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                                    Choose start date
                                                                                </p>
                                                                        }

                                                                    </div>

                                                                    {selectedStartDate !== null &&
                                                                        <Icon icon="close-icon"
                                                                            className='position-absolute top-13px right-8px cursor-pointer'
                                                                            size="20px"
                                                                            onPress={() => {
                                                                                setSelectedStartDate(null);
                                                                                setSelectedEndDate(null);
                                                                            }} />
                                                                    }
                                                                </div>
                                                            </div>

                                                            {/* End date picker */}
                                                            <div id="single-product-endDate" className='pt-3'>
                                                                <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-600 line-height-14px'>
                                                                    End date
                                                                </p>
                                                                <div className='pt-1 position-relative'>
                                                                    <div className={`${endDateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} ${selectedStartDate === null ? 'e-disable' : ''} e-display-flex gap-0-4px e-border-radius-6 padding-10px-tb px-2 cursor-pointer`}
                                                                        onClick={_handleEndDateToggle}>
                                                                        <img src={require("../../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                                            draggable={false}
                                                                            alt='calender-icon' />
                                                                        {
                                                                            selectedEndDate !== null ?
                                                                                <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                                    {format(selectedEndDate, 'dd-MM-yy')}
                                                                                </p>
                                                                                :
                                                                                <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                                    Choose end date
                                                                                </p>
                                                                        }
                                                                    </div>
                                                                    <p className='mb-0 e-error-message e-alt-font-noto-sans e-font-12 line-height-12px padding-2-top'>
                                                                        {endDateError}
                                                                    </p>

                                                                    {
                                                                        selectedEndDate !== null &&
                                                                        <Icon icon="close-icon"
                                                                            className='position-absolute top-13px right-8px cursor-pointer'
                                                                            size="20px"
                                                                            onPress={() => {
                                                                                setSelectedEndDate(null);
                                                                            }} />
                                                                    }

                                                                </div>


                                                            </div>

                                                            <PrimaryButton name="Proceed"
                                                                className={`mt-3 py-2 w-100 e-border-radius-8`}
                                                                onPress={_commitCustomDates} />
                                                        </div>
                                                    </>
                                                }
                                            </div>
                                        </div>
                                    </>
                                    :
                                    <div className='col-md-3 col-sm-4 col-4'>
                                        <CustomSelectBox label="Customer type"
                                            value={selectedCustomerType}
                                            options={activeTabFilters.customer_types || []}
                                            type={2}
                                            optional={true}
                                            className="e-bg-white e-border-radius-4 e-table-select"
                                            minHeight="34px"
                                            padding="0 8px"
                                            wrapperHeight="0px"
                                            labelClass="e-font-weight-600"
                                            isSearchable={false}
                                            onMenuOpen={_closeCustomDateMenu}
                                            selectChange={(value) => setSelectedCustomerType(value)}
                                        />
                                    </div>
                                }



                                {tabIndex === 1 &&
                                    <div className='col-md-3 col-sm-4 col-4 '>
                                        {/* <CustomSelectBox label="Product name"
                                        value={selectedProductName}
                                        options={filterData.product_name}
                                        type={2}
                                        optional={true}
                                        className="e-bg-white e-border-radius-4 e-table-select"
                                        minHeight="34px"
                                        padding="0 8px"
                                        wrapperHeight="0px"
                                        labelClass="e-font-weight-600"
                                        isSearchable={false}
                                        selectChange={(value) => setSelectedProductName(value)}
                                    /> */}
                                        <div className='position-relative' ref={dateRangeMenuRef}>
                                            <CustomSelectBox label="Date range"
                                                value={selectedDate}
                                                options={activeTabFilters.date_option || []}
                                                optional={true}
                                                type={2}
                                                className="e-bg-white e-border-radius-4 e-table-select"
                                                minHeight="34px"
                                                padding="0 8px"
                                                wrapperHeight="0px"
                                                labelClass="e-font-weight-600"
                                                isSearchable={false}
                                                onMenuOpen={_closeCustomDateMenu}
                                                components={dateRangeSelectComponents}
                                                selectChange={(value) => {
                                                    if (_isCustomDateOption(value)) {
                                                        _openCustomDateMenu();
                                                    } else {
                                                        _clearCustomDates();
                                                    }
                                                    setSelectedDate(value);
                                                }}
                                            />

                                            {dateInputMenu === true &&
                                                <>
                                                    <div className='e-bg-white e-border-1px-platinum-light e-border-radius-8 p-3 w-100 position-absolute z-3 top-68px start-0' style={{ width: "240px" }}>

                                                        {/* Start date */}
                                                        <div id="single-product-startDate ">
                                                            <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-600 line-height-14px'>
                                                                Start date
                                                            </p>

                                                            <div className='pt-1 position-relative'>
                                                                <div className={`${startDateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} e-display-flex gap-0-4px e-border-radius-6 padding-10px-tb px-2 cursor-pointer`}
                                                                    onClick={_handleStartDateToggle}>
                                                                    <img src={require("../../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                                        draggable={false}
                                                                        alt='calender-icon' />
                                                                    {
                                                                        selectedStartDate !== null ?
                                                                            <>
                                                                                <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                                    {format(selectedStartDate, 'dd-MM-yy')}
                                                                                </p>

                                                                            </>
                                                                            :
                                                                            <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                                Choose start date
                                                                            </p>
                                                                    }

                                                                </div>

                                                                {selectedStartDate !== null &&
                                                                    <Icon icon="close-icon"
                                                                        className='position-absolute top-13px right-8px cursor-pointer'
                                                                        size="20px"
                                                                        onPress={() => {
                                                                            setSelectedStartDate(null);
                                                                            setSelectedEndDate(null);
                                                                        }} />
                                                                }
                                                            </div>
                                                        </div>

                                                        {/* End date picker */}
                                                        <div id="single-product-endDate" className='pt-3'>
                                                            <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-600 line-height-14px'>
                                                                End date
                                                            </p>
                                                            <div className='pt-1 position-relative'>
                                                                <div className={`${endDateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} ${selectedStartDate === null ? 'e-disable' : ''} e-display-flex gap-0-4px e-border-radius-6 padding-10px-tb px-2 cursor-pointer`}
                                                                    onClick={_handleEndDateToggle}>
                                                                    <img src={require("../../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                                        draggable={false}
                                                                        alt='calender-icon' />
                                                                    {
                                                                        selectedEndDate !== null ?
                                                                            <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                                {format(selectedEndDate, 'dd-MM-yy')}
                                                                            </p>
                                                                            :
                                                                            <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                                Choose end date
                                                                            </p>
                                                                    }
                                                                </div>
                                                                <p className='mb-0 e-error-message e-alt-font-noto-sans e-font-12 line-height-12px padding-2-top'>
                                                                    {endDateError}
                                                                </p>

                                                                {
                                                                    selectedEndDate !== null &&
                                                                    <Icon icon="close-icon"
                                                                        className='position-absolute top-13px right-8px cursor-pointer'
                                                                        size="20px"
                                                                        onPress={() => {
                                                                            setSelectedEndDate(null);
                                                                        }} />
                                                                }

                                                            </div>


                                                        </div>

                                                        <PrimaryButton name="Proceed"
                                                            className={`mt-3 py-2 w-100 e-border-radius-8`}
                                                            onPress={_commitCustomDates} />
                                                    </div>
                                                </>
                                            }
                                        </div>

                                    </div>
                                }

                                {tabIndex === 5 &&
                                    <div className='col-md-3 col-sm-4 col-4'>
                                        <CustomSelectBox label="Status"
                                            value={selectedStatus}
                                            options={activeTabFilters.failure_status || []}
                                            type={2}
                                            optional={true}
                                            className="e-bg-white e-border-radius-4 e-table-select"
                                            minHeight="34px"
                                            padding="0 8px"
                                            wrapperHeight="0px"
                                            menuWidth="100%"
                                            labelClass="e-font-weight-600"
                                            isSearchable={false}
                                            onMenuOpen={_closeCustomDateMenu}
                                            selectChange={(value) => setSelectedStatus(value)}
                                        />
                                    </div>
                                }
                            </div>

                            {tabIndex === 2 &&
                                <div className='row pt-3'>
                                    <div className='col-md-3 col-sm-4 col-4'>
                                        <CustomSelectBox label="Customer type"
                                            value={selectedCustomerType}
                                            options={activeTabFilters.customer_types || []}
                                            type={2}
                                            optional={true}
                                            className="e-bg-white e-border-radius-4 e-table-select"
                                            minHeight="34px"
                                            padding="0 8px"
                                            wrapperHeight="0px"
                                            labelClass="e-font-weight-600"
                                            isSearchable={false}
                                            onMenuOpen={_closeCustomDateMenu}
                                            selectChange={(value) => setSelectedCustomerType(value)}
                                        />
                                    </div>
                                </div>

                            }

                            <div className='d-flex gap-3 align-items-center justify-content-end pt-3'>
                                <TextButton name="Clear all"
                                    onPress={_handleClearAll} />
                                <PrimaryButton name="Filter now"
                                    className="px-3 py-1 e-border-radius-8"
                                    onPress={() => {
                                        _getOCRList();
                                    }} />
                            </div>
                        </div>
                    }

                    <div className='pt-2 d-flex gap-1 align-items-center justify-content-end padding-12px-top px-4'>
                        <img src={require("../../../Assets/Image/CountryAdmin/scroll-animation.gif")}
                            draggable={false}
                            className='transform-rotate-270'
                            width={24}
                            alt='scroll' />
                        <p className='mb-0 text-end e-text-indigo e-alt-font-noto-sans e-font-12 e-font-weight-500 line-height-16px'>
                            Scroll horizontally for more details
                        </p>
                    </div>

                    {dataLoading ?
                        <Loader />
                        :
                        <>
                            {ocrList?.data.length !== 0 ?
                                <div className='e-data-table-wrapper-stripe e-table'>
                                    <DataTableComponent key={`ocr-table-${tabIndex}-${currentPage}-${sortField || 'none'}-${sortDirection || 'none'}`}
                                        columns={tabIndex === 1 ? INVOICE_REVIEW_COLUMN : tabIndex === 5 ? DUPLICATE_COLUMN : PAST_COLUMN}
                                        data={ocrList.data}
                                        pagination={true}
                                        paginationServer={true}
                                        paginationPerPage={ROWS_PER_PAGE}
                                        paginationTotalRows={ocrList.total_count}
                                        paginationDefaultPage={currentPage}
                                        paginationResetDefaultPage={false}
                                        sortServer={true}
                                        defaultSortFieldId={sortField}
                                        defaultSortAsc={sortDirection !== 'desc'}
                                        onSort={_handleSort}
                                        onChangePage={_handlePageChange}
                                        rowClick={(row) =>
                                            setExpandedRow(expandedRow === row.id ? null : row.id)
                                        }
                                    />
                                </div>
                                :
                                // empty screen
                                <div className='py-5 my-5'>
                                    <EmptyScreen message="No request found" />
                                </div>
                            }
                        </>
                    }
                </div>
            </div>

            {/* date picker */}
            {startDateToggle &&
                <>
                    <div className="modal fade" id="start-date-modal" data-bs-backdrop="true" tabIndex="-1" aria-labelledby="starDateModalLabel" aria-hidden="true"
                        onClick={(event) => {
                            if (event.target === event.currentTarget) {
                                _handleCloseStartDateModal();
                            }
                        }}>
                        <div className="modal-dialog modal-dialog-centered e-border-radius-24" onClick={(event) => event.stopPropagation()}>
                            <button id='start-date-modal-close'
                                className='d-none '
                                data-bs-dismiss="modal" />
                            <div className="modal-content w-358px  mx-auto border-0 bg-transparent">
                                <CustomDatePicker type="modal"
                                    disableFuture={true}
                                    selected={selectedStartDate}
                                    onSelect={_handleSelectedStartDate} />
                            </div>
                        </div>
                    </div>
                </>
            }

            {/*End date picker */}
            {endDateToggle &&
                <div className="modal fade" id="end-date-modal" data-bs-backdrop="true" tabIndex="-1" aria-labelledby="endDateModalLabel" aria-hidden="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            _handleCloseEndDateModal();
                        }
                    }} >
                    <div className="modal-dialog modal-dialog-centered e-border-radius-24" onClick={(event) => event.stopPropagation()}>
                        <button id='end-date-modal-close'
                            className='d-none '
                            data-bs-dismiss="modal" />
                        <div className="modal-content w-358px  mx-auto border-0 bg-transparent">
                            {/* <OverLay className="e-bg-overlay " isOpen={endDateToggle} onClose={_handleCloseEndDateModal} /> */}
                            <CustomDatePicker type="modal"
                                selected={selectedEndDate}
                                disableFuture={true}
                                minDate={selectedStartDate}
                                onSelect={_handleSelectedEndDate} />
                        </div>
                    </div>
                </div>
            }


            {/* Sale history selected sale */}
            {
                showSalesHistory &&
                <SalesHistoryModal data={selectedSalesHistoryData}
                    handleClose={() => setShowSalesHistory(false)} />
            }

            {/* Sale bill preview of selected sale */}
            {
                showSalesBillPreview &&
                <PreviewSalesBill file={selectedSalesHistoryData}
                    handleClose={() => {
                        setShowSalesBillPreview(false);
                        setSelectedSalesHistoryData({});
                    }} />
            }

            {/* Configure Reward Point modal*/}
            {/* {
                showConfigureRewardPointModal &&
                <ConfigureRewardPointsModal products={products}
                    handleClose={_handleCloseAndUpdateListing} />
            } */}

            {approveId !== null &&
                <ApproveUploadModal title="Approve Upload"
                    primaryButtonLabel="Approve"
                    id={approveId}
                    currencyCode={approveData?.currency_code}
                    data={approveData}
                    productCode={productCode}
                    loader={modalBtnLoader}
                    status={approveSuccess}
                    handleClose={() => {
                        setTimeout(() => {
                            setProductCode(null);
                            setApproveId(null);
                            setApproveData(null);
                        }, 200);
                    }}
                    handleSuccess={_approveProduct}
                />
            }


            {editID !== null &&
                <ApproveUploadModal title="Edit invoice items"
                    primaryButtonLabel="Update"
                    id={editID}
                    currencyCode={approveData?.currency_code}
                    productCode={productCode}
                    loader={modalBtnLoader}
                    status={revertSuccess}
                    handleClose={() => {
                        setTimeout(() => {
                            setProductCode(null);
                            setEditID(null);
                            setRevertSuccess(false);
                        }, 200);
                    }}
                    handleSuccess={_revertProduct}
                />
            }


            {rejectId !== null &&
                <RejectModal id={rejectId}
                    loader={modalBtnLoader}
                    status={approveSuccess}
                    handleClose={() => {
                        setTimeout(() => {
                            setRejectId(null);
                        }, 200);
                    }}
                    handleReject={_rejectProduct}
                />
            }

            {approveSuccess === true &&
                <SuccessModal title={modalContent?.current?.title}
                    description={modalContent?.current?.description}
                    handleClose={() => {
                        setTimeout(() => {
                            setApproveSuccess(false);

                            if (modalContent.current.type === "reject") {
                                navigate(window.location.pathname, {
                                    state: {
                                        tab_index: 3,
                                        current_page: 1
                                    }
                                });
                            } else {
                                navigate(window.location.pathname, {
                                    state: {
                                        tab_index: 2,
                                        current_page: 1
                                    }
                                });
                            }
                            // _getOCRList();
                        }, 200);
                    }} />
            }
        </section>
    )
}

export default OCRSalesAndRewardList;